<template>
  <div>
    <div class="mb-6 pb-6 border-b-2 border-grey-light flex flex-wrap items-center">
      <div>
        <h1 class="text-grey-darker text-xl font-semibold leading-normal uppercase">
          Custom Print Orders
        </h1>
        <a
          v-if="officeAccess"
          @click="$router.push('/print-order-form')"
          class="text-grey-dark text-sm font-medium leading-normal cursor-pointer no-underline hover:underline">
          &lt; Order Another Product
        </a>
      </div>
    </div>

    <loader v-if="!loaded || savingItem" />

    <!-- OFFICE HAS NO ACCESS -->
    <panel v-else-if="!officeAccess" class="pt-6">
      <strong class="mb-2">Access Denied</strong>
      <p>Your office does not currently have access to use the print order form.</p>
    </panel>

    <div v-else class="rounded-lg bg-white relative flex flex-col py-6 px-4">
      <div class="flex flex-wrap items-center leading-none pb-6 border-b-2 border-grey-light">
        <h3 class="flex w-full mb-4 lg:w-1/3 lg:mb-0">Custom Print Orders</h3>
        <div class="w-full lg:w-2/3">
          <print-sub-menu />
        </div>
      </div>
      <div class="flex flex-wrap mt-6">
        <p class="mb-6 text-sm leading-normal">If you have access to multiple office profiles, ensure that you are logged in to the office under which you would like to place your order.<br />
          To change office, click on the cog in the top right corner of your page &gt; Change Office.</p>
        <div class="mb-6 w-full lg:w-2/3">
          <div class="border border-grey-light">

            <!-- STAGE TABS -->
            <div class="w-full flex text-sm mb-6 bg-grey-lighter">
              <div @click="stage = i" class="flex flex-col w-1/3" :class="{'border-l border-r': i === 1}" v-for="(stageName, i) in stages" :key="i">
                <div class="w-full h-full px-3 pt-3 pb-5 border-t-2" :class="[(i === stage ? 'border-t-2 border-print-order-teal bg-white' : 'border-transparent bg-grey-lighter'), (i === stage ? 'cursor-default' : 'cursor-pointer')]">
                  <div class="flex h-full items-center"><div class="w-8"><span class="w-6 h-6 rounded-full flex flex-col items-center justify-center text-white" :class="i === stage ? 'bg-print-order-teal' : 'bg-grey-dark'">{{ i + 1 }}</span></div> <div class="text-sm">{{ stageName }}</div></div>
                </div>
              </div>
            </div>

            <div class="bg-yellow-lighter flex p-4 mx-4 mb-4 rounded text-sm leading-normal" v-if="!isEmpty(errorBag.errors)">
              <i class="fas fa-exclamation-triangle mr-3 fa-2x pt-2"></i>
              <div class="flex-grow">
                <strong class="block mb-3 text-sm">There were problems with your order. Please resolve these issues and try again.</strong>
                <ul class="pl-6">
                  <li v-for="(error, i) in errorBag.errors" :key="i">{{ error.join(' ') }}</li>
                </ul>
              </div>
              <i class="fas fa-times text-xl ml-3 text-grey-darker hover:text-print-order-teal cursor-pointer" @click="errorBag.clear()"></i>
            </div>

            <div class="w-full text-sm mb-2 p-4">
              <!-- STAGE FORM -->
              <component :is="stageView" v-model="orderItem" :products="products" :prices="prices" :specifications="specifications" :customFields="customFields" :propertySpec="propertySpec" :propertyFields="propertyFields" :issues="issues" :orderItem="orderItem" :categoryName="categoryName" :productName="productName" :officeId="selectedOffice.id" :fineAndCountry="isFineAndCountry" :errorBag="errorBag" @addCc="addCc" @removeCc="removeCc" />
            </div>
          </div>
        </div>

        <PrintOrderItemSummary :stage="stage" :orderItem="orderItem" :categoryName="categoryName" :productName="productName" :selectedSpecification="selectedSpecification" :artwork="artwork" :delivery="delivery" :additionalDelivery="additionalDelivery" :subtotal="subtotal" :vat="vat" :total="total" :discountType="discountType" @nextStep="nextStep" @previousStep="previousStep" @addToBasket="addToBasket" @saveItem="saveItem" />

      </div>
    </div>
  </div>
</template>

<script>
import { v2Api } from '@/http'
import { isEmpty, find } from 'lodash'
import ErrorBag from '@/ErrorBag'
import PrintSubMenu from './print-order-form/PrintSubMenu'
import PrintOrderItemSummary from './print-order-form/new-order/PrintOrderItemSummary'
import PrintOrderStage0 from './print-order-form/new-order/PrintOrderStage0'
import PrintOrderStage1 from './print-order-form/new-order/PrintOrderStage1'
import { mapState } from 'vuex'
export default {
  components: {
    PrintSubMenu,
    PrintOrderStage0,
    PrintOrderStage1,
    PrintOrderItemSummary
  },
  data () {
    return {
      itemId: null,
      categoryId: null,
      productId: null,
      loaded: false,
      loadingItem: false,
      officeAccess: false,
      products: [],
      prices: [],
      artwork: null,
      delivery: null,
      specifications: [],
      customFields: null,
      propertySpec: null,
      propertyFields: null,
      issues: null,
      selectedSpecification: null,
      orderItem: null,
      stage: 0,
      stages: [
        'Select Your Product',
        'Artwork & Notes'
      ],
      subtotal: 0,
      vat: 0,
      total: 0,
      errorBag: new ErrorBag(),
      savingItem: false,
      orderResponse: null,
      discountType: []
    }
  },
  computed: {
    ...mapState(['selectedOffice', 'profile']),
    stageView () {
      return `print-order-stage${this.stage}`
    },
    selectedProduct () {
      if (!this.orderItem.product) {
        return null
      }
      let category = this.products.find(c => c.id === this.orderItem.category)
      return category.items.find(p => p.id === this.orderItem.product)
    },
    categoryName () {
      if (!this.orderItem.category) {
        return null
      }
      let category = this.products.find(c => c.id === this.orderItem.category)
      return category.name
    },
    productName () {
      if (!this.selectedProduct) {
        return null
      }
      return this.selectedProduct.name
    },
    artworkPrice () {
      return this.artwork && this.artwork.price && this.orderItem.artwork_type_id === 1 ? this.artwork.price : 0
    },
    deliveryPrice () {
      return this.delivery && this.delivery.price ? this.delivery.price : 0
    },
    additionalDelivery () {
      if (!this.selectedProduct) {
        return null
      }
      return this.selectedProduct.additional_delivery_charges_apply
    },
    isFineAndCountry () {
      return this.selectedOffice.network_id === 7 || this.selectedOffice.network_id === 8
    }
  },
  watch: {
    selectedOffice () {
      this.loadPage()
    },
    '$route' () {
      this.loadPage()
    },
    specifications: {
      handler () {
        this.setMatchingSpecification()
        if (this.selectedSpecification !== null) {
          this.calculateTotals()
          if (this.orderItem.sale_item_price_id !== this.selectedSpecification.id) {
            this.orderItem.sale_item_price_id = this.selectedSpecification.id
          }
        }
      }
    },
    'orderItem.specification': {
      handler () {
        this.setMatchingSpecification()
        if (this.selectedSpecification !== null) {
          this.calculateTotals()
          if (this.orderItem.sale_item_price_id !== this.selectedSpecification.id) {
            this.orderItem.sale_item_price_id = this.selectedSpecification.id
          }
        }
      },
      deep: true
    },
    'orderItem.category': {
      handler (newCategoryId) {
        if (this.loadingItem) {
          return
        }
        this.loadEmptyItem(newCategoryId)
      }
    },
    'orderItem.product': {
      handler () {
        if (this.loadingItem) {
          return
        }
        this.orderItem.quantity = null
        this.orderItem.custom_field_values = []
        this.orderItem.properties = []
        this.customFields = null
        this.loadPrices()
      }
    },
    'orderItem.quantity': {
      handler () {
        this.loadSpecifications()
      }
    },
    'orderItem.artwork_type_id': {
      handler (newTypeId) {
        // Set artwork price if type is 1 (Studio)
        this.orderItem.artwork_price_id = this.artwork && newTypeId === 1 ? this.artwork.price_id : null
      }
    },
    selectedSpecification: {
      handler (newSpec) {
        if (newSpec === null) {
          this.setInitialSpecification()
        }
        this.artwork = null
        this.delivery = null
        this.propertySpec = null
        this.loadCustomFields()
        if (newSpec && newSpec.print_spec_id) {
          this.loadExtras()
        }
      }
    },
    artworkPrice: {
      handler () {
        this.calculateTotals()
      }
    },
    deliveryPrice: {
      handler () {
        this.calculateTotals()
      }
    }
  },
  created () {
    this.loadPage()
  },
  methods: {
    isEmpty,
    loadPage () {
      this.loaded = false
      this.products = []
      this.loadEmptyItem()
      this.itemId = this.$route.params.itemId ? this.$route.params.itemId : null
      this.categoryId = this.$route.query.category
      this.productId = this.$route.query.product
      this.loadProducts().then(() => {
        if (this.officeAccess) {
          this.loadItem()
        }
        this.loaded = true
      })
    },
    loadProducts () {
      return v2Api.get('custom-print-order-items/products', {
        params: {
          office_id: this.selectedOffice.id
        }
      }).then((response) => {
        this.products = response.data.products
        this.officeAccess = response.data.access
        this.discountType = response.data.discountType
      }).catch(() => {
        this.officeAccess = false
        this.loaded = true
      })
    },
    loadPrices () {
      if (this.orderItem.product === null) {
        return []
      }

      return Promise.all([
        this.loadItemPrices()
      ])
    },
    loadItemPrices () {
      return v2Api.get(`custom-print-order-items/products/${this.orderItem.product}/prices`).then((response) => {
        this.prices = response.data
      })
    },
    loadExtras () {
      return Promise.all([
        this.loadArtworkPrice(),
        this.loadDeliveryPrice(),
        this.loadMagazineProperties()
      ])
    },
    loadArtworkPrice () {
      return v2Api.get(`custom-print-order-items/products/${this.orderItem.product}/artwork`, {
        params: {
          print_spec_id: this.selectedSpecification.print_spec_id
        }
      }).then((response) => {
        this.artwork = response.data || null
        if (!this.artwork) {
          this.orderItem.artwork_type_id = 0 // None
          this.orderItem.artwork_price_id = null
        }
        if (this.artwork) {
          this.orderItem.artwork_type_id = 1 // Studio
          this.orderItem.artwork_price_id = this.artwork.price_id
        }
      })
    },
    loadDeliveryPrice () {
      return v2Api.get(`custom-print-order-items/products/${this.orderItem.product}/delivery`, {
        params: {
          print_spec_id: this.selectedSpecification.print_spec_id
        }
      }).then((response) => {
        this.delivery = response.data || null
        if (this.delivery) {
          this.orderItem.delivery_price_id = this.delivery.price_id
        }
      })
    },
    loadCustomFields () {
      v2Api.get(`sale-items/${this.orderItem.product}/custom-field-items`, {
        params: {
          network_id: this.selectedOffice.network_id
        }
      }).then((response) => {
        this.customFields = response.data
        for (let i = 0; i < this.customFields.length; i++) {
          if (find(this.orderItem.custom_field_values, (field) => field.custom_field_item_id === this.customFields[i].id)) {
            continue
          }

          if (this.customFields[i].type_id === 4) { // Multiple checkboxes
            let options = []
            for (let j = 0; j < this.customFields[i].options.length; j++) {
              options.push({
                id: this.customFields[i].options[j].id,
                value: false
              })
            }
            this.orderItem.custom_field_values.push({
              id: null,
              custom_field_item_id: this.customFields[i].id,
              value: options
            })

            continue
          }

          this.orderItem.custom_field_values.push({
            id: null,
            custom_field_item_id: this.customFields[i].id,
            value: null
          })
        }
      })
    },
    loadMagazineProperties () {
      if (this.categoryName !== 'Refined Magazine' && this.categoryName !== 'Exclusive Home Worldwide Magazine') {
        return
      }

      return v2Api.get(`custom-print-order-items/products/${this.orderItem.product}/magazine-properties`, {
        params: {
          custom_print_order_item_id: this.orderItem.id,
          office_id: this.selectedOffice.id,
          publication_type_id: this.categoryName === 'Exclusive Home Worldwide Magazine' ? 3 : 2
        }
      }).then((response) => {
        this.propertySpec = response.data.specification
        this.propertyFields = response.data.fields
        this.issues = response.data.issues
        this.orderItem.properties = response.data.properties
      })
    },
    loadSpecifications () {
      this.specifications = []
      if (this.orderItem.quantity === null) {
        return
      }

      return v2Api.get(`custom-print-order-items/products/${this.orderItem.product}/specifications`, {
        params: {
          quantity: this.orderItem.quantity
        }
      }).then((response) => {
        this.specifications = response.data
        if (this.specifications.combined_options && !isEmpty(this.specifications.combined_options)) {
          Object.keys(this.orderItem.specification).forEach(function (key) {
            this.setTopSpec(key)
          }, this)
        }
      })
    },
    setTopSpec (key) {
      // this.specifications.prices have been returned from the API in price order. First element is top spec
      let topSpec = this.specifications.prices[0].specification
      this.orderItem.specification[key] = topSpec[key]
    },
    addCc (recipient) {
      if (!recipient) {
        return
      }

      this.orderItem.notification_cc.push(recipient)
    },
    removeCc (recipient) {
      this.orderItem.notification_cc.splice(this.orderItem.notification_cc.indexOf(recipient), 1)
    },
    calculateTotals () {
      if (!this.selectedSpecification || this.selectedSpecification.price <= 0) {
        this.subtotal = 0
        this.vat = 0
        this.total = 0

        return
      }
      let subtotal = this.selectedSpecification.price + this.artworkPrice + this.deliveryPrice
      this.subtotal = subtotal
      this.vat = this.selectedOffice.network_id === 8 ? 0 : subtotal * 0.2 // No VAT for international orders
      this.total = subtotal + this.vat
    },
    setInitialSpecification () {
      this.orderItem.specification = {
        document_size: null,
        extent: null,
        orientation: null,
        stock_weight: null,
        stock_type: null,
        stock_brand: null,
        lamination: null,
        lamination_pages: null,
        cover_extent: null,
        cover_stock_weight: null,
        cover_stock_type: null,
        cover_stock_brand: null,
        cover_lamination: null,
        cover_lamination_pages: null,
        fold: null,
        binding: null
      }
      this.orderItem.sale_item_price_id = null
    },
    setMatchingSpecification () {
      this.selectedSpecification = null
      if (this.orderItem.quantity <= 0 || !this.orderItem.product || !this.specifications.prices) {
        return
      }

      if (this.specifications.prices.length === 1) {
        this.selectedSpecification = this.specifications.prices[0]

        return
      }

      for (let i = 0; i < this.specifications.prices.length; i++) {
        let keys = Object.keys(this.orderItem.specification)
        let mismatched = false
        for (let k = 0; k < keys.length; k++) {
          // If order item does not match, skip to next specification
          if (this.orderItem.specification[keys[k]] !== this.specifications.prices[i].specification[keys[k]]) {
            mismatched = true

            break
          }
        }
        if (!mismatched) {
          this.selectedSpecification = this.specifications.prices[i]
        }
      }
    },
    nextStep () {
      if (this.stage < 1) {
        this.stage++
      }
    },
    previousStep () {
      if (this.stage >= 1) {
        this.stage--
      }
    },
    addToBasket () {
      this.saveItem(true)
    },
    saveItem (addToBasket = false) {
      this.errorBag.clear()
      this.orderItem.office_id = this.selectedOffice.id
      this.orderItem.add_to_basket = addToBasket
      this.savingItem = true

      v2Api({
        method: this.itemId ? 'patch' : 'post', // Update or save new item
        url: this.itemId ? `/custom-print-order-items/${this.itemId}` : '/custom-print-order-items',
        data: this.orderItem
      }).then((response) => {
        this.receipt = response.data
        let nextPage = addToBasket ? 'basket' : 'saved-items'
        this.$router.push(`/print-order-form/${nextPage}`)
      }, (errors) => {
        this.savingItem = false
        this.errorBag.store(errors.response.data)
      })
    },
    loadItem () {
      if (this.itemId) {
        this.loadSavedItem()
      }

      if (this.productId) {
        this.loadInitialProduct()
      }
    },
    loadSavedItem () {
      if (!this.itemId) {
        return
      }

      this.loadingItem = true
      v2Api.get(`/custom-print-order-items/${this.itemId}`).then((response) => {
        this.orderItem.id = response.data.id
        this.orderItem.status_id = response.data.status_id
        this.orderItem.product = response.data.sale_item.id
        this.orderItem.category = response.data.sale_item_category.id
        this.orderItem.sale_item_price_id = response.data.sale_item_price.id
        this.orderItem.quantity = response.data.quantity
        this.orderItem.artwork_type_id = response.data.artwork_type_id
        this.orderItem.customer_reference = response.data.customer_reference
        this.orderItem.notes = response.data.notes
        this.orderItem.custom_field_values = response.data.custom_field_values
        this.orderItem.properties = response.data.properties
        this.orderItem.issue_id = response.data.issue_id
        this.orderItem.attachments = response.data.attachments
        this.orderItem.department = response.data.department
        this.loadPrices()
      }, (errors) => {
        this.errorBag.store(errors.response.data)
      }).finally(() => {
        this.loadingItem = false
      })
    },
    loadInitialProduct () {
      if (!this.productId || !this.categoryId) {
        return
      }

      this.loadingItem = true
      this.orderItem.category = parseInt(this.categoryId)
      this.orderItem.product = parseInt(this.productId)
      this.orderItem.quantity = null
      this.loadPrices().then(() => {
        this.loadingItem = false
      })
    },
    loadEmptyItem (category = null) {
      this.orderItem = {
        id: null,
        status_id: null,
        category: category,
        product: null,
        quantity: null,
        specification: {
          document_size: null,
          extent: null,
          orientation: null,
          stock_weight: null,
          stock_type: null,
          stock_brand: null,
          lamination: null,
          lamination_pages: null,
          cover_extent: null,
          cover_stock_weight: null,
          cover_stock_type: null,
          cover_stock_brand: null,
          cover_lamination: null,
          cover_lamination_pages: null,
          fold: null,
          binding: null
        },
        artwork_price_id: null,
        delivery_price_id: null,
        artwork_type_id: 0,
        customer_reference: null,
        notes: null,
        notification_cc: [],
        attachments: [],
        uploads: [],
        custom_field_values: [],
        properties: [],
        issue_id: null,
        sale_item_price_id: null,
        add_to_basket: false,
        department: 'Sales'
      }
      this.customFields = null
    }
  }
}
</script>
